import React from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Shield, 
  Monitor, 
  Cloud, 
  Headphones, 
  Server, 
  Smartphone,
  Network,
  Database,
  Lock,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const mainServices = [
    {
      icon: Monitor,
      title: 'Network Management',
      description: 'Comprehensive network monitoring, maintenance, and optimization to ensure peak performance and reliability.',
      features: [
        '24/7 network monitoring',
        'Performance optimization',
        'Security patching',
        'Bandwidth management'
      ]
    },
    {
      icon: Server,
      title: 'System Administration',
      description: 'Complete server and infrastructure management including maintenance, updates, and performance tuning.',
      features: [
        'Server management',
        'Software updates',
        'Performance monitoring',
        'Backup management'
      ]
    },
    {
      icon: Headphones,
      title: 'Helpdesk & Remote Support',
      description: 'Responsive technical support for your team with remote troubleshooting and issue resolution.',
      features: [
        '24/7 technical support',
        'Remote assistance',
        'Ticket management',
        'User training'
      ]
    },
    {
      icon: Cloud,
      title: 'Cloud Services',
      description: 'Secure cloud migration, management, and optimization for improved scalability and cost-effectiveness.',
      features: [
        'Cloud migration',
        'Multi-cloud management',
        'Cost optimization',
        'Data synchronization'
      ]
    },
    {
      icon: Shield,
      title: 'Cybersecurity Solutions',
      description: 'Enterprise-grade security measures to protect your business from cyber threats and data breaches.',
      features: [
        'Threat monitoring',
        'Firewall management',
        'Vulnerability assessments',
        'Incident response'
      ]
    },
    {
      icon: Database,
      title: 'Data Backup & Recovery',
      description: 'Comprehensive backup solutions and disaster recovery planning to protect your critical business data.',
      features: [
        'Automated backups',
        'Disaster recovery',
        'Data restoration',
        'Business continuity'
      ]
    }
  ];

  const additionalServices = [
    {
      icon: Smartphone,
      title: 'Mobile Device Management',
      description: 'Secure management of mobile devices and applications across your organization.'
    },
    {
      icon: Network,
      title: 'Network Security',
      description: 'Advanced network security measures including intrusion detection and prevention.'
    },
    {
      icon: Lock,
      title: 'Compliance Management',
      description: 'Ensure your IT infrastructure meets industry regulations and compliance standards.'
    }
  ];

  const benefits = [
    'Reduced IT costs and overhead',
    'Improved system reliability and uptime',
    'Enhanced cybersecurity posture',
    'Proactive issue prevention',
    'Scalable solutions for business growth',
    'Access to expert IT professionals'
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Comprehensive Managed IT Services
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 mb-8 leading-relaxed">
              From network management to cybersecurity, we provide complete IT solutions 
              that keep your business running smoothly and securely.
            </p>
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact">Get Your Free IT Assessment</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Our Core IT Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Comprehensive managed IT services designed to optimize your technology infrastructure, 
              enhance security, and support your business objectives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.map((service, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardHeader className="text-center pb-4">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl text-foreground">{service.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Additional IT Solutions
            </h2>
            <p className="text-lg text-muted-foreground">
              Specialized services to address specific technology needs and challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <Card key={index} className="text-center border-none shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{service.title}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                Why Choose Our Managed IT Services?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Partner with us to experience the benefits of professional IT management. Our comprehensive 
                approach ensures your technology supports your business goals while maintaining the highest 
                security standards.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                    <span className="text-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-primary to-primary-light text-primary-foreground border-none shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">Ready to Get Started?</h3>
                  <p className="text-primary-foreground/90 mb-6">
                    Let our IT experts assess your current infrastructure and create a customized 
                    managed services plan that meets your specific business needs.
                  </p>
                  <div className="space-y-4">
                    <Button asChild variant="secondary" size="lg" className="w-full">
                      <Link to="/contact">Schedule Free Consultation</Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="w-full text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                      <Link to="/cybersecurity">Explore Cybersecurity</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Transform Your IT Infrastructure Today
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
            Don't let IT challenges hold your business back. Contact us for a free consultation 
            and discover how our managed IT services can drive your success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to="/contact">Get Free Assessment</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/case-studies">View Success Stories</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;